import React, { useEffect, useState } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { formatDate } from '../../components/admin/AdminUI';
import {
  DollarSign,
  CalendarCheck,
  Clock,
  Users,
  Wrench,
  Package,
  MessageSquare,
  Phone,
  CalendarDays,
} from 'lucide-react';

const Tile = ({ icon: Icon, label, value, accent, testid }) => (
  <Card data-testid={testid} className="bg-[#0B132B] border border-[#B8860B]/25 hover:border-[#B8860B]/55 transition-colors">
    <CardContent className="p-5 flex items-center gap-4">
      <div
        className={`w-12 h-12 rounded-lg flex items-center justify-center ${
          accent || 'bg-[#B8860B]/10 border border-[#B8860B]/40'
        }`}
      >
        <Icon className="text-[#D4AF37]" size={22} />
      </div>
      <div>
        <p className="text-xs uppercase tracking-wider text-[#F4F1EA]/60">{label}</p>
        <p className="text-2xl font-bold text-[#F4F1EA]">{value}</p>
      </div>
    </CardContent>
  </Card>
);

const daysFromNow = (d) => {
  if (!d) return null;
  const target = new Date(d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
};

export const AdminDashboard = () => {
  const { authedAxios } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [arrivals, setArrivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, arrivalsRes] = await Promise.all([
          authedAxios().get('/api/admin/stats'),
          authedAxios().get('/api/admin/upcoming-arrivals'),
        ]);
        setStats(statsRes.data);
        setArrivals(arrivalsRes.data.arrivals || []);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [authedAxios]);

  if (loading) return <p className="text-[#F4F1EA]/60">Loading dashboard...</p>;
  if (error) return <p className="text-red-400">{error}</p>;

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      <div data-testid="admin-greeting">
        <h2 className="text-3xl md:text-4xl font-bold text-[#F4F1EA]">
          Hello <span className="text-[#B8860B]">Mr &amp; Mrs Scott</span>
        </h2>
        <p className="text-[#F4F1EA]/60 mt-1">
          Here's what's happening at Creek Bottom Ranch &amp; Resort today.
        </p>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile
          testid="stat-todays-checkins"
          icon={CalendarCheck}
          label="Today's Check-ins"
          value={stats.todays_checkins}
        />
        <Tile
          testid="stat-week-revenue"
          icon={DollarSign}
          label="Last 7 Days Revenue"
          value={`$${stats.week_revenue.toFixed(2)}`}
        />
        <Tile
          testid="stat-pending-bookings"
          icon={Clock}
          label="Pending Bookings"
          value={stats.pending_bookings}
        />
        <Tile
          testid="stat-total-subscribers"
          icon={Users}
          label="Newsletter Subscribers"
          value={stats.total_subscribers}
        />
        <Tile
          testid="stat-total-bookings"
          icon={CalendarCheck}
          label="Total Bookings"
          value={stats.total_bookings}
        />
        <Tile
          testid="stat-total-rentals"
          icon={Wrench}
          label="Total Rentals"
          value={stats.total_rentals}
        />
        <Tile
          testid="stat-equipment-count"
          icon={Package}
          label="Equipment Items"
          value={stats.equipment_count}
        />
        <Tile
          testid="stat-new-inquiries"
          icon={MessageSquare}
          label="New Inquiries"
          value={stats.new_inquiries}
        />
      </div>

      {/* Upcoming arrivals */}
      <Card
        data-testid="admin-upcoming-arrivals"
        className="bg-[#0B132B] border border-[#B8860B]/30"
      >
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-[#B8860B]/20 flex items-center gap-3">
            <CalendarDays className="text-[#B8860B]" size={22} />
            <div>
              <h3 className="text-lg font-bold text-[#F4F1EA]">Upcoming Arrivals</h3>
              <p className="text-xs text-[#F4F1EA]/60">Next 7 days · {arrivals.length} guest{arrivals.length === 1 ? '' : 's'}</p>
            </div>
          </div>

          {arrivals.length === 0 ? (
            <p className="px-5 py-10 text-center text-[#F4F1EA]/60">
              No arrivals scheduled in the next 7 days.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#050814] text-[#B8860B] uppercase tracking-wider text-xs">
                  <tr>
                    <th className="text-left p-3">Check-in</th>
                    <th className="text-left p-3">Guest</th>
                    <th className="text-left p-3">Site</th>
                    <th className="text-left p-3">Phone</th>
                    <th className="text-left p-3">Nights</th>
                  </tr>
                </thead>
                <tbody>
                  {arrivals.map((a) => {
                    const dleft = daysFromNow(a.check_in);
                    const label =
                      dleft === 0 ? 'Today' : dleft === 1 ? 'Tomorrow' : `In ${dleft} days`;
                    return (
                      <tr
                        key={a.id}
                        data-testid={`arrival-row-${a.id}`}
                        className="border-t border-[#B8860B]/10 hover:bg-[#050814]/60"
                      >
                        <td className="p-3">
                          <p className="text-[#F4F1EA] font-semibold">{formatDate(a.check_in)}</p>
                          <p
                            className={`text-xs ${
                              dleft === 0 ? 'text-[#B8860B]' : 'text-[#F4F1EA]/60'
                            }`}
                          >
                            {label}
                          </p>
                        </td>
                        <td className="p-3 text-[#F4F1EA]">
                          <p className="font-semibold">
                            {a.first_name} {a.last_name}
                          </p>
                          <p className="text-xs text-[#F4F1EA]/60">{a.email}</p>
                        </td>
                        <td className="p-3 text-[#F4F1EA]/85">
                          {a.site_type}
                          <p className="text-xs text-[#F4F1EA]/60">
                            {a.guests} guest{a.guests === 1 ? '' : 's'}
                          </p>
                        </td>
                        <td className="p-3">
                          <a
                            href={`tel:${(a.phone || '').replace(/\D/g, '')}`}
                            className="inline-flex items-center gap-1 text-[#B8860B] hover:text-[#F4F1EA]"
                          >
                            <Phone size={14} /> {a.phone}
                          </a>
                        </td>
                        <td className="p-3 text-[#F4F1EA]/85">{a.nights}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
